const VIEWPORT_PADDING_PX = 16;
const GAP_PX = 2;
const DROPDOWN_MAX_WIDTH_REM = 35;

function remPx(): number {
  return parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
}

function viewportWidth(): number {
  return window.visualViewport?.width ?? window.innerWidth;
}

export function getFilterDropdownMaxWidthPx(viewportWidthPx: number): number {
  const maxPanelWidth = DROPDOWN_MAX_WIDTH_REM * remPx();
  return Math.min(maxPanelWidth, viewportWidthPx - VIEWPORT_PADDING_PX * 2);
}

/**
 * Dropdown is `left: 0` on the filter button (CSS). Only shrink width so the
 * right edge stays inside the viewport — never shift left away from the button.
 */
export function getFilterDropdownWidthForRoot(
  root: HTMLElement,
  viewportWidthPx = viewportWidth(),
  padding = VIEWPORT_PADDING_PX,
): number {
  const rootRect = root.getBoundingClientRect();
  const maxPanelWidth = getFilterDropdownMaxWidthPx(viewportWidthPx);
  const availableToRight = viewportWidthPx - padding - rootRect.left;

  return Math.max(0, Math.min(maxPanelWidth, availableToRight));
}

export function resetFilterDropdownPosition(panel: HTMLElement): void {
  panel.style.removeProperty('--filter-dropdown-max-width');
  panel.style.width = '';
  panel.style.maxWidth = '';
  panel.style.left = '';
  panel.style.right = '';
}

/**
 * Pixels to scroll down so an absolutely positioned panel below `trigger` fits in view.
 */
export function getFilterDropdownScrollDelta(
  trigger: HTMLElement,
  panel: HTMLElement,
  viewportHeight: number,
  padding = VIEWPORT_PADDING_PX,
): number {
  const triggerRect = trigger.getBoundingClientRect();
  const panelHeight = panel.offsetHeight;
  if (panelHeight <= 0) {
    return 0;
  }

  const dropdownBottom = triggerRect.bottom + GAP_PX + panelHeight;
  const maxBottom = viewportHeight - padding;

  if (dropdownBottom <= maxBottom) {
    return 0;
  }

  return dropdownBottom - maxBottom;
}

export function applyFilterDropdownWidth(
  root: HTMLElement,
  panel: HTMLElement,
): number {
  const width = getFilterDropdownWidthForRoot(root);
  panel.style.setProperty('--filter-dropdown-max-width', `${width}px`);
  panel.style.width = `${width}px`;
  panel.style.maxWidth = `${DROPDOWN_MAX_WIDTH_REM}rem`;
  panel.style.left = '';
  panel.style.right = '';
  return width;
}

/**
 * Keep dropdown left-aligned to its button; clamp width to viewport; scroll if clipped below.
 */
export function ensureFilterDropdownVisible(
  root: HTMLElement,
  trigger: HTMLElement,
  panel: HTMLElement,
): void {
  resetFilterDropdownPosition(panel);
  applyFilterDropdownWidth(root, panel);

  const delta = getFilterDropdownScrollDelta(
    trigger,
    panel,
    window.innerHeight,
  );
  if (delta <= 0) {
    return;
  }

  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches;

  window.scrollBy({
    top: delta,
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
}
